import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, List, Input, Row, Col, Tag, Typography, Spin, message, Space, Button
} from 'antd';
import { SearchOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons';

// 接口定义
interface Announcement {
  id: string;
  title: string;
  releaseDate: number | string; // Unix Millisecond Timestamp - 兼容字符串和数字格式
  exchange: string;
  url: string;
}

interface Exchange {
  id: string;
  name: string;
  color: string;
  icon: string;
}

// 交易所静态数据
const exchanges: Exchange[] = [
  { id: 'binance', name: 'Binance', color: '#F0B90B', icon: '🟡' },
  { id: 'okx', name: 'OKX', color: '#000000', icon: '⚫' },
  { id: 'bybit', name: 'Bybit', color: '#00C087', icon: '🟢' },
  { id: 'bitget', name: 'Bitget', color: '#00A0B0', icon: '🔵' },
  { id: 'gateio', name: 'Gate.io', color: '#2354E6', icon: '🔵' }
];

// API基础URL
const API_BASE_URL = 'https://api.0xluoye.xyz/api/announcements';

// ExchangeCard 组件
interface ExchangeCardProps {
  exchange: Exchange;
  announcements: Announcement[];
  filterKeyword: string;
}

const ExchangeCard: React.FC<ExchangeCardProps> = ({ exchange, announcements, filterKeyword }) => {
  // 筛选公告
  const filteredAnnouncements = announcements.filter(item =>
    item.title.toLowerCase().includes(filterKeyword.toLowerCase())
  );

  // 格式化日期 - 兼容字符串和数字格式
  const formatDate = (timestamp: number | string): string => {
    const numericTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
    const date = new Date(numericTimestamp);

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '无效';
    }

    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card
      size="small"
      title={
        <span style={{ fontSize: '14px', fontWeight: '600' }}>
          {exchange.icon} {exchange.name}
        </span>
      }
      extra={
        <span style={{ fontSize: '12px', color: '#999' }}>
          {filteredAnnouncements.length} 条
        </span>
      }
      style={{ height: '400px' }}
      bodyStyle={{ padding: '8px', height: 'calc(100% - 57px)', overflow: 'auto' }}
    >
      {filteredAnnouncements.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#999',
          fontSize: '13px'
        }}>
          {announcements.length === 0 ? '暂无公告' : '没有匹配的公告'}
        </div>
      ) : (
        <List
          size="small"
          dataSource={filteredAnnouncements}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '6px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              <div style={{ width: '100%' }}>
                <Typography.Link
                  href={item.url}
                  target="_blank"
                  style={{
                    fontSize: '13px',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '4px'
                  }}
                  title={item.title}
                >
                  {item.title}
                </Typography.Link>
                <div style={{
                  fontSize: '11px',
                  color: '#999',
                  textAlign: 'right'
                }}>
                  {formatDate(item.releaseDate)}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

const AnnouncementsPage: React.FC = () => {
  // 状态管理
  const [announcements, setAnnouncements] = useState<Record<string, Announcement[]>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterKeyword, setFilterKeyword] = useState<string>('');

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数：统一时间戳格式
  const normalizeTimestamp = (timestamp: number | string): number => {
    if (typeof timestamp === 'string') {
      const parsed = parseInt(timestamp, 10);
      // 检查是否为有效的时间戳
      return isNaN(parsed) ? 0 : parsed;
    }
    return timestamp || 0;
  };

  // 格式化日期 - 兼容字符串和数字格式
  const formatDate = (timestamp: number | string): string => {
    const numericTimestamp = normalizeTimestamp(timestamp);
    const date = new Date(numericTimestamp);

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '无效日期';
    }

    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 获取所有交易所公告数据
  const fetchAllAnnouncements = async () => {
    setIsLoading(true);
    
    try {
      // 并发请求所有交易所的数据
      const promises = exchanges.map(async (exchange) => {
        try {
          const response = await fetch(`${API_BASE_URL}/${exchange.id}?page=1&limit=20`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          // 调试日志：检查时间戳格式
          if (data && data.length > 0) {
            console.log(`${exchange.name} 数据示例:`, {
              title: data[0].title,
              releaseDate: data[0].releaseDate,
              releaseDateType: typeof data[0].releaseDate
            });
          }
          return { exchangeId: exchange.id, data: data || [] };
        } catch (error) {
          console.error(`获取 ${exchange.name} 公告失败:`, error);
          return { exchangeId: exchange.id, data: [] };
        }
      });

      const results = await Promise.allSettled(promises);
      const newAnnouncements: Record<string, Announcement[]> = {};

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { exchangeId, data } = result.value;
          
          // 确保每个公告都带有正确的交易所信息
          const processedData = data.map((item: Announcement) => ({
            ...item,
            exchange: exchangeId // 统一设置exchange字段为当前交易所ID
          }));
          
          newAnnouncements[exchangeId] = processedData;
        }
      });

      setAnnouncements(newAnnouncements);
      message.success('公告数据加载完成');
    } catch (error) {
      console.error('获取公告数据失败:', error);
      message.error('获取公告数据失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 派生状态：合并后的最新公告
  const mergedAnnouncements = useMemo(() => {
    const allAnnouncements: Announcement[] = [];
    const seenTitles = new Set<string>();

    // 展平所有交易所的公告并去重
    Object.entries(announcements).forEach(([exchangeId, exchangeAnnouncements]) => {
      exchangeAnnouncements.forEach(announcement => {
        // 确保公告的exchange字段与数据源匹配
        const announcementWithCorrectExchange = {
          ...announcement,
          exchange: exchangeId // 确保exchange字段使用正确的交易所ID
        };
        
        // 使用title+exchange作为唯一键去重
        const key = `${announcement.title}_${exchangeId}`;
        if (!seenTitles.has(key)) {
          seenTitles.add(key);
          allAnnouncements.push(announcementWithCorrectExchange);
        }
      });
    });

    // 按发布时间降序排序并截取前20条 - 兼容字符串和数字格式
    return allAnnouncements
      .sort((a, b) => {
        const timeA = normalizeTimestamp(a.releaseDate);
        const timeB = normalizeTimestamp(b.releaseDate);
        return timeB - timeA;
      })
      .slice(0, 20);
  }, [announcements]);

  // 筛选公告
  const filterAnnouncements = (announcementList: Announcement[]): Announcement[] => {
    if (!filterKeyword.trim()) return announcementList;
    
    return announcementList.filter(item =>
      item.title.toLowerCase().includes(filterKeyword.toLowerCase())
    );
  };

  // 获取交易所信息
  const getExchangeInfo = (exchangeId: string): Exchange => {
    return exchanges.find(ex => ex.id === exchangeId) || exchanges[0];
  };

  // 组件初始化
  useEffect(() => {
    fetchAllAnnouncements();
  }, []);

  if (isLoading) {
    return (
      <div className="container-responsive" style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', color: '#666' }}>正在加载交易所公告...</div>
      </div>
    );
  }

  return (
    <div className="container-responsive">
      {/* 顶部筛选区域 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--spacing-2xl)'
      }}>
        <div>
          <h1 style={{
            fontSize: 'var(--font-xl)',
            fontWeight: 'bold',
            color: '#333',
            margin: 0,
            marginBottom: '8px'
          }}>
            交易所公告
          </h1>
          <div style={{ fontSize: '13px', color: '#666' }}>
            已加载 {Object.values(announcements).reduce((total, list) => total + list.length, 0)} 条公告
          </div>
        </div>

        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAllAnnouncements}
            loading={isLoading}
            style={buttonStyle}
          >
            刷新
          </Button>
          <Input.Search
            placeholder="筛选公告标题..."
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
            style={{
              width: 300,
              ...componentStyle
            }}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Space>
      </div>

      {/* 最新聚合区域 */}
      <Card 
        title="最新公告" 
        className="spacing-responsive"
        extra={
          <span style={{ fontSize: '13px', color: '#666' }}>
            共 {filterAnnouncements(mergedAnnouncements).length} 条
          </span>
        }
      >
        {filterAnnouncements(mergedAnnouncements).length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999'
          }}>
            {mergedAnnouncements.length === 0 ? (
              <div>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>📢</div>
                <div>暂无公告数据</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>🔍</div>
                <div>没有找到匹配的公告</div>
              </div>
            )}
          </div>
        ) : (
          <List
            size="small"
            dataSource={filterAnnouncements(mergedAnnouncements)}
            renderItem={(item) => {
              const exchangeInfo = getExchangeInfo(item.exchange.toLowerCase());
              return (
                <List.Item
                  style={{ padding: '8px 0' }}
                  extra={
                    <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>
                      {formatDate(item.releaseDate)}
                    </span>
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Tag
                      color={exchangeInfo.color}
                      style={{
                        marginRight: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {exchangeInfo.icon} {exchangeInfo.name}
                    </Tag>
                    <Typography.Link
                      href={item.url}
                      target="_blank"
                      style={{
                        fontSize: '14px',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.title}
                    </Typography.Link>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {/* 交易所分类区域 */}
      <div style={{ marginTop: 'var(--spacing-2xl)' }}>
        <h2 style={{
          fontSize: 'var(--font-lg)',
          fontWeight: 'bold',
          color: '#333',
          marginBottom: 'var(--spacing-lg)'
        }}>
          分类浏览
        </h2>

        <Row gutter={[16, 16]}>
          {exchanges.map((exchange) => (
            <Col xs={24} sm={12} md={8} key={exchange.id}>
              <ExchangeCard
                exchange={exchange}
                announcements={announcements[exchange.id] || []}
                filterKeyword={filterKeyword}
              />
            </Col>
          ))}
        </Row>
      </div>

      {/* 使用提示 */}
      <Card size="small" style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        marginTop: '20px'
      }}>
        <div style={{ color: '#52c41a' }}>
          <strong>💡 使用提示：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>页面自动聚合 Binance、OKX、Bybit、Bitget 四大交易所的最新公告</li>
            <li>最新公告区域显示所有交易所的公告，按发布时间排序</li>
            <li>分类浏览区域可以查看各个交易所的独立公告列表</li>
            <li>支持关键词实时筛选，可快速找到感兴趣的公告内容</li>
            <li>点击公告标题可在新标签页中打开原文链接</li>
            <li>数据每次加载获取各交易所最新20条公告进行展示</li>
            <li>已兼容不同交易所的时间格式差异，确保正确排序显示</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default AnnouncementsPage;
